import { Component, computed, inject, input, LOCALE_ID, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FilterSortingComponent } from '../../../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import { FilterInput } from '../../../../../shared/ui/shared-filters/filter-input/filter-input';
import { FilterMultiSelectComponent } from '../../../../../shared/ui/shared-filters/filter-multi-select-component/filter-multi-select-component';
import { FilterDateComponent } from '../../../../../shared/ui/shared-filters/filter-date-component/filter-date-component';
import { PharmacyAutoComplete } from '../../../../admin-dashboard-component/invoice-list-component/shared/pharmacy-auto-complete/pharmacy-auto-complete';
import { PharmacyAutoCompleteApiItem } from '../../../../../services/pharmace-auto-complete/pharmace-auto-complete.service';
import { CreatorAutoComplete } from './creator-auto-complete';
import {
  PaymentRequestService,
  PaymentRequestCreatorAutoCompleteApiItem,
} from '../../../../../services/payment-request/payment-request.service';

export interface AdminPaymentRequestFilters {
  requestId?: string;
  pharmacyId?: number;
  createdBy?: string;
  createdById?: number;
  paymentMethodIds?: string[];
  statusIds?: string[];
  createdOnFrom?: string;
  createdOnTo?: string;
  orderBy?: string;
  orderDir?: 'ASC' | 'DESC';
}

@Component({
  selector: 'app-admin-payment-requests-filter',
  standalone: true,
  imports: [
    FormsModule,
    FilterSortingComponent,
    FilterInput,
    FilterMultiSelectComponent,
    FilterDateComponent,
    PharmacyAutoComplete,
    CreatorAutoComplete,
  ],
  templateUrl: './payment-requests-filter.html',
  styleUrl: './payment-requests-filter.scss',
})
export class AdminPaymentRequestsFilter implements OnInit {
  private locale = inject(LOCALE_ID);
  private paymentRequestService = inject(PaymentRequestService);

  filters = input.required<AdminPaymentRequestFilters>();
  statusOptions = input<{ id: string; titleEn: string; titleAr: string }[]>([]);

  applyFilters = output<AdminPaymentRequestFilters>();
  resetFilters = output();
  toggleSortOrder = output();

  // 1. Payment Request ID & Validation
  requestId = signal('');
  requestIdInvalid = signal(false);

  
  requestIdErrorMessage = computed(() =>
    this.isArabic()
      ? 'يجب أن يحتوي رقم طلب الدفع على أرقام فقط.'
      : 'Payment Request ID must contain numbers only.'
  );

  dateErrorMessage = computed(() =>
    this.isArabic()
      ? 'تاريخ البداية لا يمكن أن يكون بعد تاريخ النهاية.'
      : 'From date cannot be after To date.'
  );

  // 2. Pharmacy Autocomplete
  selectedPharmacyId = signal<number | null>(null);
  pharmacyClear = signal(0);

  // 3. Created By Autocomplete
  selectedCreatorId = signal<number | null>(null);
  creatorClear = signal(0);

  paymentMethodOptions = signal<{ id: string; titleEn: string; titleAr: string }[]>([]);
  selectedPaymentMethodIds = signal<string[]>([]);
  showPaymentMethodDropdown = signal(false);

  selectedStatusIds = signal<string[]>([]);
  showStatusDropdown = signal(false);

  createdOnFrom = signal<string | null>(null);
  createdOnTo = signal<string | null>(null);
  createdDateInvalid = signal(false);

  isArabic = computed(() => this.locale.startsWith('ar'));

  pharmacyLabel = computed(() => (this.isArabic() ? 'الصيدلية' : 'Pharmacy'));
  pharmacyPlaceholder = computed(() => (this.isArabic() ? 'ابحث عن الصيدلية' : 'Search pharmacy'));
  noPharmacyResults = computed(() => (this.isArabic() ? 'لا توجد نتائج' : 'No results found'));

  creatorLabel = computed(() => (this.isArabic() ? 'منشئ الطلب' : 'Created By'));
  creatorPlaceholder = computed(() =>
    this.isArabic() ? 'اسم المندوب أو موزع التوصيل' : 'Delivery or Rep Name',
  );
  noCreatorResults = computed(() => (this.isArabic() ? 'لا توجد نتائج' : 'No results found'));

  sortOptions = computed(() => [
    { value: 'CREATION_DATE', labelEn: 'Creation Date', labelAr: 'تاريخ الإنشاء' },
    { value: 'AMOUNT', labelEn: 'Amount', labelAr: 'المبلغ' },
  ]);

  ngOnInit(): void {
    this.loadPaymentMethods();

    const initial = this.filters();
    if (initial) {
      if (initial.requestId) this.requestId.set(initial.requestId);
      if (initial.pharmacyId) this.selectedPharmacyId.set(initial.pharmacyId);
      if (initial.createdById) this.selectedCreatorId.set(initial.createdById);
      if (initial.paymentMethodIds) this.selectedPaymentMethodIds.set(initial.paymentMethodIds);
      if (initial.statusIds) this.selectedStatusIds.set(initial.statusIds);
      if (initial.createdOnFrom) this.createdOnFrom.set(initial.createdOnFrom);
      if (initial.createdOnTo) this.createdOnTo.set(initial.createdOnTo);
    }
  }

  loadPaymentMethods(): void {
    this.paymentRequestService.getAllPaymentTypes().subscribe({
      next: (res) => {
        this.paymentMethodOptions.set(
          res.list.map((item) => ({
            id: item.id.toString(),
            titleEn: item.titleEn,
            titleAr: item.titleAr,
          })),
        );
      },
      error: (err) => {
        console.error('Failed to load payment types', err);
      },
    });
  }

  onPharmacySelected(item: PharmacyAutoCompleteApiItem) {
    this.selectedPharmacyId.set(item.id);
  }

  onCreatorSelected(item: PaymentRequestCreatorAutoCompleteApiItem) {
    this.selectedCreatorId.set(item.id);
  }

  togglePaymentMethodDropdown() {
    const currentState = this.showPaymentMethodDropdown();
    this.closeAllDropdowns();
    this.showPaymentMethodDropdown.set(!currentState);
  }

  toggleStatusDropdown() {
    const currentState = this.showStatusDropdown();
    this.closeAllDropdowns();
    this.showStatusDropdown.set(!currentState);
  }

  closeAllDropdowns() {
    this.showPaymentMethodDropdown.set(false);
    this.showStatusDropdown.set(false);
  }

  onToggleSortOrder() {
    this.toggleSortOrder.emit();
  }

  onApplyFilters() {
    const trimmedReqId = this.requestId().trim();
    
    // Validate Numeric Input for Request ID (Numeric characters 0-9 only)
    const isReqIdInvalid = !!trimmedReqId && !/^\d+$/.test(trimmedReqId);
    this.requestIdInvalid.set(isReqIdInvalid);

    // Validate Date Range
    const createdInvalid = !!(
      this.createdOnFrom() &&
      this.createdOnTo() &&
      this.createdOnFrom()! > this.createdOnTo()!
    );
    this.createdDateInvalid.set(createdInvalid);

    
    if (isReqIdInvalid || createdInvalid) return;

    this.applyFilters.emit({
      requestId: trimmedReqId || undefined,
      pharmacyId: this.selectedPharmacyId() ?? undefined,
      createdById: this.selectedCreatorId() ?? undefined,
      paymentMethodIds: this.selectedPaymentMethodIds().length ? this.selectedPaymentMethodIds() : undefined,
      statusIds: this.selectedStatusIds().length ? this.selectedStatusIds() : undefined,
      createdOnFrom: this.createdOnFrom() ?? undefined,
      createdOnTo: this.createdOnTo() ?? undefined,
    });
  }

  onResetFilters() {
    this.requestId.set('');
    this.requestIdInvalid.set(false);
    this.selectedPharmacyId.set(null);
    this.pharmacyClear.update((v) => v + 1);
    this.selectedCreatorId.set(null);
    this.creatorClear.update((v) => v + 1);
    this.selectedPaymentMethodIds.set([]);
    this.selectedStatusIds.set([]);
    this.createdOnFrom.set(null);
    this.createdOnTo.set(null);
    this.createdDateInvalid.set(false);
    this.closeAllDropdowns();
    this.resetFilters.emit();
  }
}